import React, { forwardRef, MouseEventHandler, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useDebounce } from 'react-use';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import Icon from '../../icon/Icon';
import { Button } from '../../button';

const WORKER_SRC = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC;

/**
 * PdfViewer
 * 
 * Features:
 * - Has a max width, if the viewport is smaller than this, it resizes to fill 100%
 * - Fully scrollable within the container it is in
 * - Handles outside clicks properly
 */
const PdfViewer = (({ url, onClickOutside, defaultWidth = 800 }: { url: string, onClickOutside?: () => void, defaultWidth?: number }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const downloadActionRef = useRef<HTMLButtonElement>(null);
  const [numPages, setNumPages] = useState(0);

  const setPageWrapperMaxHeight = useCallback((height: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.maxHeight = `${height}px`;
    }
  }, []);

  const [pageWidth, setPageWidth] = useState(defaultWidth);
  const [debouncedPageWidth] = useDebounceValue(pageWidth, 100);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return () => { };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === container) {
          const { height, width } = entry.contentRect;
          setPageWrapperMaxHeight(height);
          setPageWidth(Math.min(width, defaultWidth));
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, [defaultWidth, setPageWrapperMaxHeight]);

  const onLoadSuccess = useCallback((pdf: pdfjs.PDFDocumentProxy) => {
    setNumPages(pdf.numPages);

    const container = containerRef.current;
    if (container) {
      setPageWrapperMaxHeight(container.clientHeight);
    }
  }, [setPageWrapperMaxHeight]);

  const onClick: MouseEventHandler = useCallback((ev) => {
    if (!innerRef.current?.contains(ev.target as Node) && !downloadActionRef.current?.contains(ev.target as Node)) {
      onClickOutside?.();
    }
  }, [onClickOutside]);

  return (
    <div ref={containerRef} className='pdf-viewer-container' onClick={onClick}>
      <Document
        file={url}
        className='pdf-viewer-document'
        onLoadSuccess={onLoadSuccess}
      >
        <div ref={scrollContainerRef} className='pdf-viewer-scroll-container'>
          <div ref={innerRef} className='pdf-viewer-inner'>
            {Array.from({ length: numPages }).map((_, index) => (
              <Page
                key={index}
                pageNumber={index + 1}
                className='pdf-viewer-page'
                // scale={1.5}
                width={pageWidth}
              />
            ))}
          </div>
          <div className='pdf-viewer-actions' style={{
            minWidth: `calc(0.5 * (100% - ${pageWidth}px))`,
          }}>
            {/* @ts-expect-error */}
            <Button variant='outline-primary' className='pdf-viewer-action-button' type='button' icon='file-download' href={url} download ref={downloadActionRef}>
              Download PDF
            </Button>
          </div>
        </div>
      </Document>
    </div>
  );
});

export default PdfViewer;
